import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	Fragment,
} from 'react';
import PropTypes from 'prop-types';
import {
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	useReactTable,
} from '@tanstack/react-table';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';

import FilterPlate from 'src/components/ui/FilterPlate';
import {
	getSortLabel,
	VALUE_TYPE,
} from 'src/components/ui/FilterPlate/constants';
import { useFilterPopover } from 'src/components/ui/FilterPopover';
import { useSort } from 'src/hooks/filters/useSort';
import RowRender from 'src/components/ui/RowRender';
import TablePagination from 'src/components/ui/TablePagination';

import ScrollX from './styled/ScrollX';
import FilterCell from './styled/FilterCell';

const emptyRect = {
	x: 0,
	y: 0,
	top: 0,
	left: 0,
	bottom: 0,
	right: 0,
	width: 0,
	height: 0,
	toJSON: () => ({}),
};

const DefaultTable = ({
	data,
	columns,
	striped,
	scrollable,
	selectedFilters,
	pagination,
	FilterPlateRP = FilterPlate,
}) => {
	const filterPopover = useFilterPopover();
	const { getSort } = useSort();
	const activeSort = getSort();
	const cellRefs = useRef({});
	const lastRectRef = useRef(null);

	const table = useReactTable({
		data,
		columns,
		defaultColumn: {
			size: 200,
			minSize: 80,
			maxSize: 300,
		},
		getCoreRowModel: getCoreRowModel(),
		getRowCanExpand: (row) => !!row.subRows.length,
		getSubRows: (row) => row.groupLinksAsGroup,
		getExpandedRowModel: getExpandedRowModel(),
	});

	const [localTarget, setLocalTarget] = useState(null);
	const [localApplying, setLocalApplying] = useState(false);
	const localApplyingRef = useRef(false);
	const localDataAtApplyRef = useRef(data);

	const useExternal = Boolean(filterPopover);

	const filterTarget = useExternal ? filterPopover.filterTarget : localTarget;
	const isApplying = useExternal ? filterPopover.isApplying : localApplying;
	const isApplyingRef = useExternal
		? filterPopover.isApplyingRef
		: localApplyingRef;
	const activeFilterId = filterTarget?.id ?? null;

	const closeFilter = useCallback(() => {
		if (useExternal) {
			filterPopover.closeFilter();
			return;
		}

		setLocalTarget(null);
		setLocalApplying(false);
		localApplyingRef.current = false;
	}, [useExternal, filterPopover]);

	const startApplying = useCallback(() => {
		if (useExternal) {
			filterPopover.startApplying();
			return;
		}

		localApplyingRef.current = true;
		localDataAtApplyRef.current = data;
		setLocalApplying(true);
	}, [useExternal, filterPopover, data]);

	useEffect(() => {
		if (useExternal || !localApplying) return;

		if (data !== localDataAtApplyRef.current) {
			closeFilter();
		}
	}, [useExternal, localApplying, data, closeFilter]);

	useEffect(() => {
		if (!filterTarget) return undefined;

		const onKeyDown = (e) => {
			if (e.key === 'Escape' && !isApplyingRef.current) {
				closeFilter();
			}
		};

		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [filterTarget, closeFilter, isApplyingRef]);

	useEffect(() => {
		if (!filterTarget) return undefined;

		const onMouseDown = (e) => {
			if (isApplyingRef.current) return;

			const anchor = activeFilterId
				? cellRefs.current[activeFilterId]
				: null;
			const popper = document.querySelector(
				'[data-filter-popper="true"]',
			);

			if (anchor?.contains(e.target)) return;
			if (popper?.contains(e.target)) return;

			// Autocomplete / DatePicker render lists in portals outside the filter popper
			if (
				e.target.closest?.(
					'.MuiAutocomplete-popper, .MuiPickersPopper-root, .MuiModal-root',
				)
			) {
				return;
			}

			closeFilter();
		};

		document.addEventListener('mousedown', onMouseDown);
		return () => document.removeEventListener('mousedown', onMouseDown);
	}, [filterTarget, activeFilterId, closeFilter, isApplyingRef]);

	const handleFilterClick = useCallback(
		(columnData, rect) => {
			if (isApplyingRef.current) return;
			if (!columnData) return;

			if (rect) {
				lastRectRef.current = rect;
			}

			if (activeFilterId === columnData.id) {
				closeFilter();
				return;
			}

			if (useExternal) {
				filterPopover.openFilter(columnData);
				return;
			}

			setLocalTarget({
				...columnData,
				value: selectedFilters?.[columnData.code]?.value,
			});
		},
		[
			useExternal,
			filterPopover,
			selectedFilters,
			isApplyingRef,
			activeFilterId,
			closeFilter,
		],
	);

	const virtualAnchor = useMemo(() => {
		if (!activeFilterId) return null;

		return {
			getBoundingClientRect: () => {
				const el = cellRefs.current[activeFilterId];

				if (el) {
					lastRectRef.current = el.getBoundingClientRect();
					return lastRectRef.current;
				}

				return lastRectRef.current ?? emptyRect;
			},
			contextElement: cellRefs.current[activeFilterId] ?? undefined,
		};
	}, [activeFilterId]);

	let tableNode = (
		<TableContainer component={Paper}>
			<Table>
				<TableHead>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								const filter = header.column.columnDef?.filter;
								const { minSize } = header.column.columnDef;
								const filterCode = filter?.code || header.id;

								return (
									<FilterCell
										key={header.id}
										ref={(node) => {
											if (node) {
												cellRefs.current[header.id] =
													node;
											} else {
												delete cellRefs.current[
													header.id
												];
											}
										}}
										isActive={activeFilterId === header.id}
										isHoverable={!!filter}
										onClick={(e) => {
											handleFilterClick(
												filter
													? {
														id: header.id,
														code: filterCode,
														...filter,
													}
													: undefined,
												e.currentTarget.getBoundingClientRect(),
											);
										}}
										sx={{
											minWidth: minSize || 50,
										}}
										{...header.column.columnDef.meta}
									>
										{header.isPlaceholder
											? null
											: flexRender(
												header.column.columnDef
													.header,
												header.getContext(),
											)}

										{selectedFilters?.[filterCode]
											?.text && (
											<Typography
												variant="body2"
												color="primary"
												whiteSpace="nowrap"
											>
												{
													selectedFilters[filterCode]
														.text
												}
											</Typography>
										)}

										{activeSort?.field === filterCode && (
											<Typography
												variant="body2"
												color="primary"
												whiteSpace="nowrap"
											>
												{getSortLabel(
													activeSort.direction,
												)}
											</Typography>
										)}
									</FilterCell>
								);
							})}
						</TableRow>
					))}
				</TableHead>
				<TableBody {...(striped && { className: 'striped' })}>
					{table.getRowModel().rows.map((row) => {
						const expandable = row.getCanExpand();
						const isExpanded = row.getIsExpanded();
						const cells = row.getVisibleCells();
						const expandHandler = expandable
							? row.getToggleExpandedHandler()
							: undefined;

						return (
							<Fragment key={row.id}>
								<RowRender
									cells={cells}
									depth={row.depth}
									subCount={row.subRows.length}
									expandable={expandable}
									onToggleExpand={expandHandler}
									isExpanded={isExpanded}
								/>
							</Fragment>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);

	const popoverNode =
		filterTarget && virtualAnchor ? (
			<Popper
				open
				anchorEl={virtualAnchor}
				placement="bottom-start"
				style={{ zIndex: 1300 }}
			>
				<Box
					data-filter-popper="true"
					sx={{
						width:
							filterTarget?.valueType === VALUE_TYPE.date
								? 360
								: 280,
					}}
				>
					<FilterPlateRP
						onComplete={startApplying}
						isLoading={isApplying}
						{...filterTarget}
					/>
				</Box>
			</Popper>
		) : null;

	if (scrollable) {
		tableNode = <ScrollX>{tableNode}</ScrollX>;
	}

	return (
		<Stack gap={2}>
			{tableNode}
			{pagination && <TablePagination {...pagination} />}
			{popoverNode}
		</Stack>
	);
};

DefaultTable.propTypes = {
	striped: PropTypes.bool,
	scrollable: PropTypes.bool,
	data: PropTypes.arrayOf(PropTypes.any),
	columns: PropTypes.arrayOf(PropTypes.any),
	selectedFilters: PropTypes.objectOf(
		PropTypes.shape({
			text: PropTypes.string,
		}),
	),
	pagination: PropTypes.shape(TablePagination.propTypes),
	FilterPlateRP: PropTypes.elementType,
};

export default DefaultTable;
